import { useEffect, useCallback } from 'react';
import { Button, Alert, Typography } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useEssayAutoSave, type EssaySaveStatus } from '../hooks/useEssayAutoSave';

import MilkdownEditor from './MilkdownEditor';

const { Text } = Typography;

interface EssayEditorProps {
  slug: string;
  initialContent: string;
  contentHash?: string | null;
  editorRevision: number;
  onExternalUpdate: boolean;
  onReloadExternal: () => void;
  onDismissExternal: () => void;
  onExitEdit: () => void;
}

function SaveStatusIndicator({ status }: { status: EssaySaveStatus }) {
  if (status === 'idle') return null;
  const color =
    status === 'error' || status === 'conflict'
      ? '#ff4d4f'
      : status === 'saving'
      ? '#888'
      : status === 'saved'
      ? '#52c41a'
      : '#555';
  const text =
    status === 'saving'
      ? 'Saving...'
      : status === 'saved'
      ? 'Saved'
      : status === 'error'
      ? 'Error'
      : status === 'conflict'
      ? 'Conflict'
      : '';
  return <Text style={{ color, fontSize: 12 }}>{text}</Text>;
}

export default function EssayEditor({
  slug,
  initialContent,
  contentHash,
  editorRevision,
  onExternalUpdate,
  onReloadExternal,
  onDismissExternal,
  onExitEdit,
}: EssayEditorProps) {
  const { handleContentChange, saveNow, forceOverwrite, saveStatus } =
    useEssayAutoSave({ slug, contentHash });

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveNow();
      }
      if (
        e.key === 'Escape' &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        saveNow();
        onExitEdit();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveNow, onExitEdit]);

  const onContentChange = useCallback(
    (markdown: string) => {
      handleContentChange(markdown);
    },
    [handleContentChange]
  );

  return (
    <div>
      {/* External update banner */}
      {onExternalUpdate && (
        <Alert
          type="info"
          message="This essay was updated externally"
          action={
            <span style={{ display: 'flex', gap: 8 }}>
              <Button size="small" type="primary" icon={<SyncOutlined />} onClick={onReloadExternal}>
                Reload
              </Button>
              <Button size="small" onClick={onDismissExternal}>
                Keep editing
              </Button>
            </span>
          }
          style={{ marginBottom: 8 }}
          closable={false}
        />
      )}

      {/* Conflict banner */}
      {saveStatus === 'conflict' && (
        <Alert
          type="warning"
          message="Content was modified externally since you started editing"
          action={
            <span style={{ display: 'flex', gap: 8 }}>
              <Button size="small" type="primary" icon={<SyncOutlined />} onClick={onReloadExternal}>
                Reload
              </Button>
              <Button size="small" onClick={forceOverwrite}>
                Overwrite
              </Button>
            </span>
          }
          style={{ marginBottom: 8 }}
          closable={false}
        />
      )}

      {/* Save status */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <SaveStatusIndicator status={saveStatus} />
      </div>

      {/* Editor */}
      <MilkdownEditor
        key={`essay-${slug}-${editorRevision}`}
        initialContent={initialContent}
        onChange={onContentChange}
        autoFocus
        showToolbar
      />
    </div>
  );
}
